import { useState } from 'react'
import { MoonStar, RotateCcw } from 'lucide-react'
import { useResetChat, useSnoozeChat, type Chat } from '../lib/api'
import { hasActiveChatWork } from '../lib/chatListFilters'
import SnoozePicker, { LANE_SNOOZE_PRESETS } from './SnoozePicker'

/**
 * Reset and Snooze beside a numbered lane row (Mark, 1 November 2026). They
 * reuse the lane header's mutations, so behaviour matches the conversation
 * view; neither action navigates.
 */
export default function LaneRowActions({ chat, title }: { chat: Chat; title: string }) {
  const reset = useResetChat(chat.id)
  const snooze = useSnoozeChat(chat.id)
  const [picking, setPicking] = useState(false)
  const busy = reset.isPending || snooze.isPending
  const name = title || chat.id
  const snoozed = chat.is_snoozed === true

  function resetLane() {
    if (busy) return
    // A tap in a scrolling list is easier to misfire than the header button,
    // so only interrupting live work asks first.
    if (hasActiveChatWork(chat) && !window.confirm(`${name} is working. Reset it and stop that work?`)) return
    reset.mutate()
  }

  const base = 'inline-flex min-h-11 min-w-9 items-center justify-center rounded-lg px-1.5 disabled:opacity-50'
  return (
    <div className="mr-1 shrink-0">
      <div className="flex items-center">
        <button
          type="button"
          aria-label={`Reset ${name}`}
          title="Reset this lane — clears the queue and restarts the agent process."
          disabled={busy}
          onClick={resetLane}
          className={`${base} text-stone-500 hover:bg-stone-100 dark:text-stone-400 dark:hover:bg-stone-800`}
        >
          <RotateCcw className={`h-3.5 w-3.5 ${reset.isPending ? 'animate-spin' : ''}`} aria-hidden />
        </button>
        <button
          type="button"
          aria-label={`${snoozed ? 'Reschedule snooze for' : 'Snooze'} ${name}`}
          title={snoozed ? 'Reschedule this lane snooze.' : 'Snooze this lane.'}
          disabled={busy}
          onClick={() => setPicking(true)}
          className={snoozed
            ? `${base} text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/40`
            : `${base} text-stone-500 hover:bg-stone-100 dark:text-stone-400 dark:hover:bg-stone-800`}
        >
          <MoonStar className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      {reset.isError && <p role="alert" className="pb-2 text-xs text-red-600 dark:text-red-400">
        Could not reset. Tap to retry.
      </p>}
      {snooze.isError && <p role="alert" className="pb-2 text-xs text-red-600 dark:text-red-400">
        Could not snooze. Tap the moon to retry.
      </p>}
      {picking && <SnoozePicker
        title={snoozed ? 'Reschedule lane' : 'Snooze lane'}
        presets={LANE_SNOOZE_PRESETS}
        footerNote="Hides this lane from the normal lane list until the chosen time. It stays available under the Snoozed filter."
        onCancel={() => setPicking(false)}
        onPick={(wakeAt) => {
          setPicking(false)
          snooze.mutate({ wake_at: wakeAt, reason: 'manual lane snooze' })
        }}
      />}
    </div>
  )
}
